import { FormattedMessage, useIntl } from "react-intl";

import { BoxHeader } from "@/components/design/BoxHeader";
import { Switch } from "@/components/design/Switch";

import { isSectionVisible, toggleHiddenSection, type ResultSectionLabel } from "./result-sections";

/**
 * Die Auswahl, welche Abschnitte eines Ergebnisses angezeigt werden.
 *
 * EINE KOMPONENTE FUER ZWEI ORTE: das geoeffnete Ergebnis (nachtraeglich aendern) und der
 * Speichern-Dialog (beim Anlegen festlegen). Beide zeigen dieselben Zeilen in derselben
 * Reihenfolge, weil sie dieselbe Abschnittsliste bekommen — sonst waere die Einstellung im
 * Dialog eine andere Sprache als die am Ergebnis.
 *
 * SCHALTER SAGEN "ANZEIGEN", NICHT "AUSBLENDEN". Gespeichert wird die Liste der ausgeblendeten
 * Abschnitte (siehe result-sections.ts), aber am Bildschirm ist "an = sichtbar" die Richtung,
 * die niemand zweimal liest. Die Umkehrung passiert hier, an genau einer Stelle.
 */
export function ResultSectionPicker<T extends string>({
  sectionIds,
  labels,
  hidden,
  onChange,
  variant = "box",
  disabled = false,
}: {
  sectionIds: readonly T[];
  labels: Record<T, ResultSectionLabel>;
  hidden: readonly string[] | null | undefined;
  onChange: (next: string[]) => void;
  /** "box" für das geöffnete Ergebnis, "plain" für die Einbettung in einen Dialog. */
  variant?: "box" | "plain";
  disabled?: boolean;
}) {
  const intl = useIntl();
  const visibleCount = sectionIds.filter((id) => isSectionVisible(id, hidden)).length;

  const rows = (
    <ul>
      {sectionIds.map((id) => {
        const label = intl.formatMessage(labels[id], labels[id].values);
        const visible = isSectionVisible(id, hidden);
        const toggle = () => { if (!disabled) onChange(toggleHiddenSection(hidden, id)); };
        return (
          // Schalter LINKS am Text, nicht rechtsbuendig: bei zehn kurzen Labels stand der
          // Schalter meterweit vom Wort entfernt und man las Zeile fuer Zeile neu nach, welcher
          // zu welchem gehoert. Die Zeile traegt die Menu-Row-Tokens aus UI_CONVENTIONS
          // (rounded-md, px-2 py-2, hover:bg-ink/[0.06]) und ist als Ganzes anklickbar.
          //
          // Bewusst KEIN <MenuItem>, obwohl die Konvention es fuer Popover-Zeilen vorschreibt:
          // MenuItem ist ein <button>, und der Switch darin waere ein Button im Button. Die
          // Zeile ist deshalb ein <li> mit onClick, der Switch traegt mit `stopPropagation`
          // seinen eigenen Klick und bleibt der Tastaturweg samt Fokusring und ARIA-Label.
          <li
            key={id}
            onClick={toggle}
            className="flex cursor-pointer items-center gap-3 rounded-md px-2 py-2 hover:bg-ink/[0.06]"
          >
            <Switch
              disabled={disabled}
              checked={visible}
              onChange={toggle}
              stopPropagation
              ariaLabel={intl.formatMessage(
                {
                  id: "match.result.sectionPicker.toggleAria",
                  defaultMessage: "{section} anzeigen",
                },
                { section: label },
              )}
              id={`result-section-${id}`}
            />
            <span className={`min-w-0 truncate text-smycles-meta ${visible ? "text-ink" : "text-ink"}`}>
              {label}
            </span>
          </li>
        );
      })}
    </ul>
  );

  if (variant === "plain") return rows;

  return (
    <div className="box bg-surface p-5">
      <BoxHeader
        title={
          <FormattedMessage id="match.result.sectionPicker.header" defaultMessage="Angezeigte Abschnitte" />
        }
        right={
          <span className="smycles-field-label">
            <FormattedMessage
              id="match.result.sectionPicker.count"
              defaultMessage="{visible} von {total}"
              values={{ visible: visibleCount, total: sectionIds.length }}
            />
          </span>
        }
      />
      {rows}
    </div>
  );
}
